import React from 'react';
import { Helmet } from 'react-helmet-async';
import { USState, CensusMetric } from '../types';
import './LocationContent.css';

interface LocationContentProps {
  selectedState: USState | null;
  metric: CensusMetric;
  countySlug?: string | null;
}

const formatCountyName = (slug: string) => {
  return slug.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

const LocationContent: React.FC<LocationContentProps> = ({ selectedState, metric, countySlug }) => {
  if (!selectedState) {
    return (
      <div className="location-content">
        <Helmet>
          <title>Census Atlas - US Demographic Data Map</title>
          <meta name="description" content={`Interactive map of United States demographic, economic, and education data based on the latest census metrics. View ${metric.label.toLowerCase()} by county and state.`} />
        </Helmet>
        <section className="seo-content">
          <h2>United States {metric.label} Data</h2>
          <p>
            Explore interactive maps of {metric.label.toLowerCase()} across the United States. 
            This demographic intelligence platform visualizes {metric.description.toLowerCase()} 
            Click on any state to view detailed county-level metrics and data breakdowns.
          </p>
          <p>
            The Census Atlas provides an accessible way to analyze US demographics, economics, 
            and educational attainment. Select from different datasets in the sidebar to 
            update the map colors dynamically.
          </p>
        </section>
      </div>
    );
  }

  const countyName = countySlug ? formatCountyName(countySlug) : null;
  const locationName = countyName ? `${countyName}, ${selectedState.name}` : selectedState.name;

  const title = `${locationName} ${metric.label} Data - Census Atlas`;
  const description = `Explore interactive maps and data for ${metric.label.toLowerCase()} in ${locationName}. Detailed demographic intelligence and census metrics.`;

  return (
    <div className="location-content">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
      </Helmet>
      <section className="seo-content">
        <h2>{locationName} {metric.label}</h2>
        <p>
          Welcome to the demographic profile for <strong>{locationName}</strong>. 
          The data representation highlights the {metric.label.toLowerCase()} across various regions in {locationName}. 
          {metric.description ? ` This metric represents ${metric.description.toLowerCase()}` : ''}
        </p>
        <p>
          Use our interactive census intelligence platform to understand the distribution of {metric.label.toLowerCase()} 
          throughout {locationName}'s geographic boundaries. By visualizing this data, researchers, students, 
          and policymakers can identify regional trends and demographic shifts.
        </p>
      </section>
    </div>
  );
};

export default LocationContent;
